import type {
  SELECTED_CONTENT,
  CONTENT_STRUCTURE_WITH_DATA,
  UUID,
  CONTENT_STRUCTURE_SLOT,
  CONTENT_STRUCTURE,
  CONTENT_STRUCTURE_ROOT,
  RAW_CLIPBOARD_PAYLOAD,
  SIDEBAR_OPTIONS,
} from '../types/Blocks'
import type { ContextMenuMethods } from 'primevue/contextmenu'
import type { MenuItem } from 'primevue/menuitem'
import {
  appendBlock,
  collectKeys,
  deleteBlockByKey,
  duplicateBlockByKey,
  findBlockInContents,
  hasStructureThisKey,
} from './utils'
import { useStorage } from '@vueuse/core'
import { Content } from '@@/shared-components/content/settings'
import { CONTENT_VERSION, DEFAULT_SIDEBAR_OPTIONS, EMPTY_OBJECT } from './const'
import { useContentSerializer } from '@@/shared-components/serializers/content'

const ContentInstance = Content.getInstance()

const dragOverKey = shallowRef<UUID | null>(null)

export const COLS = ContentInstance.COLS

export class ContentTypes {
  private static instance: ContentTypes
  private types: CONTENT_TYPE_REGISTRY

  private constructor() {
    this.types = {
      article: '/admin/articles/',
      infobox: '/admin/infoboxes/',
      product: '/admin/products/',
      branch: '/admin/branches/',
      tag: '/admin/tags/',
    }
  }

  public static getInstance(): ContentTypes {
    if (!ContentTypes.instance) {
      ContentTypes.instance = new ContentTypes()
    }
    return ContentTypes.instance
  }

  public getTypes(): CONTENT_TYPE_REGISTRY {
    return this.types
  }

  public addType(key: keyof CONTENT_TYPE_REGISTRY, value: ValueOf<CONTENT_TYPE_REGISTRY>): void {
    this.types[key] = value as never
  }
}

export { CONTENT_VERSION } from './const'

export const useContent = (contextMenu?: Ref) => {
  const {
    $i18n: { t },
  } = useNuxtApp()
  let pageId: string
  const { searchingBlock } = useInteractions()
  const allContents = useState<{
    [uuid: string]: SELECTED_CONTENT
  }>('all-contents', () => ({}))
  const slotNames = useState<{
    [ContentUUID: string]: {
      [slotKey: string]: string
    }
  }>('slot-names', () => ({}))
  const selectedContent = useState<SELECTED_CONTENT | null>('selected-content', () => null)
  const selectedBlock = useState<CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT | null>(
    'selected-container',
    () => null
  )
  const selectedInteraction = useState<CONTENT_STRUCTURE_WITH_DATA['key'] | null>(
    'selected-interaction',
    () => null
  )
  const selectedChild = useState<CONTENT_STRUCTURE_WITH_DATA | null>('selected-child', () => null)
  const sidebarOptions = useState<SIDEBAR_OPTIONS>('sidebar-options', () =>
    DEFAULT_SIDEBAR_OPTIONS()
  )
  const itemResizing = useState<string | null>('item-resizing', () => null)
  const structureOpened = useState<boolean>('structure-opened', () => false)
  const hoveredItem = useState<CONTENT_STRUCTURE_WITH_DATA['key'] | undefined>('hovered-item')
  const possibleDrop = useState<string | number | null>('possible-drop', () => null)
  const hiddenBlocks = useState<{ [key: UUID]: boolean }>('hidden-blocks', () => ({}))
  const dragging = useState('dragging', () => false)
  const itemDragged = useState<CONTENT_STRUCTURE | 'empty' | null>('dragged-item', () => null)
  const draggedIndex = useState<number>('dragged-index', () => -1)
  const hideEditTools = useState('hide-edit-tools', () => false)
  const formkitEditing = useState('formkit-editing', () => false)
  const placingNotAssignedChild = useState<{
    type: ValueOf<BlockChildsRegistry>
    key: UUID
  } | null>('placing-not-assigned-child', () => null)

  const allBlockKeys = computed(() =>
    collectKeys(selectedContent.value?.content?.structure ?? EMPTY_OBJECT)
  )
  // Editační režim layoutů: přihlášený uživatel s `article.update.any`.
  // Čte sdílený auth stav useAuth() — ten plní buď vestavěný auth plugin,
  // nebo vlastní auth projektu (sync ve watchEffect).
  const auth = useAuth()
  // Projektový vypínač editačního režimu (a s ním globálního top panelu /
  // context menu). Default true; projekt může nastavit na false pro určité
  // části appky přes useState('is-edit-content-enabled'). Blokový side panel
  // se řídí `selectedContent`, takže funguje dál i když je tohle false.
  const isEditContentEnabled = useState<boolean>('is-edit-content-enabled', () => true)
  const isEditContentActive = computed(
    () =>
      isEditContentEnabled.value &&
      !!auth.value?.user &&
      !!auth.value?.permissions?.some((p: string) => p === 'article.update.any')
  )

  const globalContextMenu = useState<ContextMenuMethods>('context-menu')
  const selectBlockCallbacks = useState<{
    [key: string]: (block: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT) => void
  }>('select-block-callbacks', () => ({}))
  const contextMenuItem = useState<CONTENT_STRUCTURE_WITH_DATA | null>(
    'context-menu-item',
    () => null
  )
  const oneClick = useState('one-click', () => false)
  const clipboardBlock = useStorage(
    'clipboard-content',
    () => {
      if (import.meta.client) {
        return localStorage.getItem('clipboard-content')
      }
    },
    import.meta.client ? localStorage : undefined,
    {
      serializer: {
        read: (v) => {
          if (v) {
            try {
              const data = JSON.parse(v)
              if (data.CONTENT_VERSION === CONTENT_VERSION) {
                return data.blocks
              }
            } catch (e) {
              return null
            }
          }
          return null
        },
        write: (blocks) => {
          if (blocks) {
            return JSON.stringify({
              CONTENT_VERSION,
              blocks,
            })
          }
        },
      },
    }
  )
  // Raw tvar clipboardu (structure + structureContent + contents). Záměrně jiný
  // klíč než legacy 'clipboard-content' — staré verze shared-components tak
  // zůstávají nedotčené. Verze se při read nezahazuje, migruje se až při paste
  // (deserialize pouští refactor chain podle structure.version).
  const clipboardRaw = useStorage<RAW_CLIPBOARD_PAYLOAD | null>(
    'clipboard-content-raw',
    null,
    import.meta.client ? localStorage : undefined,
    {
      serializer: {
        read: (v) => {
          if (v) {
            try {
              const data = JSON.parse(v)
              if (data?.structure && data?.structureContent !== undefined) {
                return data
              }
            } catch (e) {
              return null
            }
          }
          return null
        },
        write: (payload) => (payload ? JSON.stringify(payload) : ''),
      },
    }
  )

  const hasClipboard = computed(() => !!clipboardRaw.value || !!clipboardBlock.value)

  const isSelectedBlockRoot = computed(() =>
    selectedContent.value
      ? Object.values(
          allContents.value[selectedContent.value?.uuid]!.content.structure.children
        ).some((v) => v.children.some((ch) => ch.key === selectedBlock.value?.key))
      : false
  )

  const onSelectBlock = (
    block: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT,
    root?: CONTENT_STRUCTURE_WITH_DATA
  ) => {
    if (searchingBlock.value) {
      Object.values(selectBlockCallbacks.value).forEach((cb) => {
        cb(block)
      })
    } else if (block && root?.type !== 'select') {
      const nuxtApp = useNuxtApp()
      nuxtApp.runWithContext(async () => {
        hideEditTools.value = false
        selectedBlock.value = block
      })
    }
  }

  const onUnselectBlock = () => {
    if (!selectedBlock.value) {
      document.dispatchEvent(new CustomEvent('shake-blocks'))
      selectedBlock.value = null
    } else {
      const nuxtApp = useNuxtApp()
      nuxtApp.runWithContext(async () => {
        const { data: linkTypes, suspense } = useQuery(linkTypesQuery())
        await suspense()
        if (
          selectedBlock.value &&
          Object.values(selectedBlock.value.interactions ?? {}).some(
            (i) => !i.object && linkTypes.value?.[i.type!]?.lookupUrl
          )
        ) {
          return
        }
        selectedBlock.value = null
      })
    }
  }

  // useConfirm() je inject-based — z click/shortcut handlerů (mimo setup) funguje
  // jen uvnitř runWithContext (app-level provide z ConfirmationService). Bez něj
  // hodí výjimku, kterou root onErrorCaptured tiše spolkne a dialog se neotevře.
  const openStructure = () => {
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(() => {
      structureOpened.value = true
      const confirm = useConfirm()
      confirm.require({
        group: 'structure',
        position: 'right',
        modal: false,
        onHide: () => {
          structureOpened.value = false
        },
      })
    })
  }

  const closeStructure = () => {
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(() => {
      const confirm = useConfirm()
      structureOpened.value = false
      confirm.close()
    })
  }

  // Na stránce může být víc contentů (article, infobox…) — vlastníka bloku
  // dohledej podle klíče, ne přes selectedContent (ten může být jiný content).
  const findContentByBlockKey = (key?: UUID): SELECTED_CONTENT | null => {
    if (!key) return null
    return (
      Object.values(allContents.value).find((c) =>
        hasStructureThisKey(c.content.structure as unknown as CONTENT_STRUCTURE_ROOT, key)
      ) ?? null
    )
  }

  const copyBlock = (block: CONTENT_STRUCTURE_WITH_DATA) => {
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(() => {
      const { serializeBlockToClipboard } = useContentSerializer()
      const ownerContent = findContentByBlockKey(block.key) ?? selectedContent.value
      clipboardRaw.value = serializeBlockToClipboard(
        block,
        (ownerContent?.content.structure as unknown as CONTENT_STRUCTURE_ROOT | undefined)?.locale
      )
    })
  }

  const pasteBlock = (blockToPaste: CONTENT_STRUCTURE_WITH_DATA) => {
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(() => {
      const { hydrateClipboardPayload } = useContentSerializer()
      // Raw klíč má přednost; legacy 'clipboard-content' (hydratovaný blok ze
      // starší verze shared-components) slouží jako fallback.
      const block = clipboardRaw.value
        ? hydrateClipboardPayload(clipboardRaw.value)
        : clipboardBlock.value
      const targetContent = findContentByBlockKey(blockToPaste.key) ?? selectedContent.value
      if (!block || !targetContent) return
      appendBlock(
        targetContent.content.structure.children,
        blockToPaste.key!,
        block,
        blockToPaste.type === 'placeholder'
      )
    })
  }

  const contextMenuItems = computed<MenuItem[]>(() => {
    return [
      // data.type nemusí mít blok který není v structureContent (není v jazyce)
      ...(contextMenuItem.value?.data?.type
        ? [
            {
              label: t('Duplikovat'),
              shortcut: { label: '⌘+D', bind: 'command+d' },
              command: () => {
                duplicateBlockByKey(
                  selectedContent.value!.content.structure.children!,
                  contextMenuItem.value!.key!
                )
              },
            },
            {
              label: t('Kopírovat'),
              shortcut: { label: '⌘+C', bind: 'command+c' },
              command: () => {
                copyBlock(contextMenuItem.value!)
              },
            },
          ]
        : []),
      {
        label: t('Vložit'),
        disabled: !hasClipboard.value,
        shortcut: { label: '⌘+V', bind: 'command+v' },
        command: () => {
          pasteBlock(contextMenuItem.value!)
        },
      },
      {
        label: t('Struktura'),
        shortcut: { label: '⌘+I', bind: 'command+i' },
        command: () => {
          openStructure()
        },
      },
      // Pokud se změní pořadí upravit i v AppContextMenu kde to bere poslední prvek z tohoto pole
      {
        label: t('Odstranit'),
        shortcut: { label: '⌦', bind: 'del' },
        command: () => {
          deleteBlockByKey(
            selectedContent.value!.content.structure.children!,
            contextMenuItem.value!.key!
          )
          if (selectedBlock.value?.key === contextMenuItem.value!.key!) {
            onUnselectBlock()
          }
        },
      },
    ]
  })

  watch(
    () => contextMenu?.value,
    (newContextMenu) => {
      if (newContextMenu) {
        globalContextMenu.value = newContextMenu
      }
    }
  )

  const openContextMenu = (event: MouseEvent, block: CONTENT_STRUCTURE_WITH_DATA) => {
    contextMenuItem.value = block
    globalContextMenu?.value?.show(event)
  }

  watch(selectedContent, (newValue) => {
    if (newValue) {
      nextTick(() => {
        newValue.containerRef?.value?.scrollIntoView()
      })
    }
  })

  const onSelectBlockClick = (
    cb: (block: CONTENT_STRUCTURE_WITH_DATA | CONTENT_STRUCTURE_SLOT) => void
  ) => {
    if (!pageId) {
      pageId = useId()
    }
    selectBlockCallbacks.value = {
      ...selectBlockCallbacks.value,
      [pageId]: cb,
    }
  }

  const vueInstance = getCurrentInstance()

  if (vueInstance) {
    onBeforeUnmount(() => {
      delete selectBlockCallbacks.value[pageId]
    })
  }

  const onDragOver = (block: CONTENT_STRUCTURE_WITH_DATA) => {
    dragOverKey.value = block.key!
  }

  const onDragLeave = () => {
    dragOverKey.value = null
  }

  const structure = computed({
    get: () => selectedContent.value?.content.structure,
    set: (value) => {
      if (value) {
        selectedContent.value!.content.structure = value
        if (selectedBlock.value) {
          const block = findBlockInContents(allContents.value, selectedBlock.value!.key!)
          if (block) {
            selectedBlock.value = block
          }
        }
      }
    },
  })

  // Programové copy/paste pro externí tooling (přenos bloků mezi projekty):
  // copy vrátí raw payload, paste umí payload z jiného projektu přijmout.
  if (import.meta.client && !window.contentClipboard) {
    window.contentClipboard = {
      copy: (blockKey?: UUID) => {
        const block = blockKey
          ? findBlockInContents(allContents.value, blockKey)
          : selectedBlock.value
        if (!block || !('data' in block)) return null
        copyBlock(block as CONTENT_STRUCTURE_WITH_DATA)
        return clipboardRaw.value
      },
      paste: (payload?: RAW_CLIPBOARD_PAYLOAD | null, targetKey?: UUID) => {
        if (payload) {
          clipboardRaw.value = payload
        }
        const target = targetKey
          ? findBlockInContents(allContents.value, targetKey)
          : selectedBlock.value
        if (target) {
          pasteBlock(target as CONTENT_STRUCTURE_WITH_DATA)
        }
      },
    }
  }

  return {
    placingNotAssignedChild,
    isEditContentActive,
    oneClick,
    allBlockKeys,
    dragging,
    selectedInteraction,
    copyBlock,
    onSelectBlock,
    onUnselectBlock,
    draggedIndex,
    dragOverKey,
    sidebarOptions,
    onDragOver,
    onDragLeave,
    possibleDrop,
    selectedChild,
    clipboardBlock,
    clipboardRaw,
    hasClipboard,
    slotNames,
    hideEditTools,
    formkitEditing,
    itemResizing,
    structure,
    structureOpened,
    openStructure,
    closeStructure,
    onSelectBlockClick,
    selectBlockCallbacks,
    hoveredItem,
    hiddenBlocks,
    allContents,
    selectedContent,
    isSelectedBlockRoot,
    selectedBlock,
    contextMenuItem,
    openContextMenu,
    pasteBlock,
    contextMenuItems,
    itemDragged,
  }
}
